import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

export type ThemePreference = "light" | "dark" | "system";

export interface ThemeState {
  preference: ThemePreference;
}

const THEME_STORAGE_KEY = "bazaarx_theme_preference";

function readInitialPreference(): ThemePreference {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
}

const initialState: ThemeState = {
  preference: readInitialPreference(),
};

/**
 * Theme lives in Redux, not a standalone context, because
 * docs/architecture/frontend-architecture.md names Theme as global state
 * alongside Authentication and Socket. `src/hooks/useTheme.ts` is what
 * resolves `'system'` against the OS setting and applies the result to the
 * document — this slice only tracks the user's *stated* preference.
 *
 * Persisting to `localStorage` directly inside the reducer is a deliberate,
 * pragmatic exception to normal reducer purity — it's simple, well-understood,
 * and low-risk for a single string preference. `auth`'s token persistence
 * (a more sensitive value) deliberately does NOT follow this pattern — see
 * `src/lib/token-storage.ts` and the note in `authSlice.ts`.
 */
const themeSlice = createSlice({
  name: "theme",
  initialState,
  reducers: {
    setPreference: (state, action: PayloadAction<ThemePreference>) => {
      state.preference = action.payload;
      localStorage.setItem(THEME_STORAGE_KEY, action.payload);
    },
  },
});

export const { setPreference } = themeSlice.actions;
export default themeSlice.reducer;
